/** App entry point. Initializes Sentry error tracking and renders the root React tree. */
import React from "react";
import { createRoot, hydrateRoot } from 'react-dom/client';

import App from "./App";
import "./index.css";
import "./ui/bun.css";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Defer Sentry initialization until after first paint to avoid blocking LCP
if (typeof window !== 'undefined') {
  const initSentry = () => {
    import("@sentry/react").then((Sentry) => {
      return import("./utils/sentryScrubber").then(({ beforeSend, beforeBreadcrumb }) => {
        Sentry.init({
          dsn: process.env.REACT_APP_SENTRY_DSN,
          integrations: [Sentry.browserTracingIntegration()],
          tracesSampleRate: 1.0,
          replaysSessionSampleRate: 0.1,
          replaysOnErrorSampleRate: 1.0,
          sendDefaultPii: false,
          beforeSend,
          beforeBreadcrumb,
        });
      });
    });
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(initSentry);
  } else {
    setTimeout(initSentry, 2000);
  }
}

// Defer Core Web Vitals reporting until after first paint (same idle pattern as Sentry).
// Sends LCP/INP/CLS/FCP/TTFB to the anonymous ingest endpoint via sendBeacon when available.
if (typeof window !== 'undefined') {
  const initWebVitals = () => {
    import('web-vitals').then(({ onLCP, onINP, onCLS, onFCP, onTTFB }) => {
      const send = (m) => {
        if (!m) return;
        const payload = JSON.stringify({
          vital_name: m.name,
          value: m.value,
          rating: m.rating,
          navigation_type: m.navigationType,
          page_path: window.location.pathname,
          device_kind: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        });
        const url = '/api/v1/web-vitals';
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
        } else {
          fetch(url, {
            method: 'POST',
            body: payload,
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
          }).catch(() => {});
        }
      };
      onLCP(send);
      onINP(send);
      onCLS(send);
      onFCP(send);
      onTTFB(send);
    }).catch(() => { /* never break the page if web-vitals fails to load */ });
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(initWebVitals);
  } else {
    setTimeout(initWebVitals, 2500);
  }
}

const container = document.getElementById("root");
// Wrap the entire app in DndProvider to enable drag-and-drop functionality across all components. 
// Using HTML5Backend for standard browser support.
const app = (
  <DndProvider backend={HTML5Backend}>
    <App />
  </DndProvider>
);

// If pre-rendered HTML exists (from react-snap), hydrate instead of full render
if (container.hasChildNodes()) {
  hydrateRoot(container, app);
} else {
  createRoot(container).render(app);
}
